import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import posService from 'Services/posService'
import { PosAccountType } from 'Models/posAccountType'
import { checkSearchTransactionInputType } from 'Helpers'

interface PosTerminalsTableProps {
  pageNumber: number
  pageSize: number
  searchTerm: string
  posFrom?: string
  posTo?: string
  posTerminalId?: string
  posProvider?: string
  onTotalChange: (total: number, pageSize?: number) => void
  onLoadingChange?: (loading: boolean) => void
}

const PosTerminalsTable: React.FC<PosTerminalsTableProps> = ({
  pageNumber,
  pageSize,
  searchTerm,
  posFrom,
  posTo,
  posTerminalId,
  posProvider,
  onTotalChange,
  onLoadingChange,
}) => {
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<PosAccountType[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchPosAccounts = async () => {
      const query = searchTerm.trim()
      try {
        setIsLoading(true)
        onLoadingChange?.(true)
        if (query) {
          const searchType = checkSearchTransactionInputType(query)
          let endpoint = ''
          if (searchType === 'PhoneNumber') {
            endpoint = `customerMsisdn=${encodeURIComponent(query)}`
          } else if (searchType === 'name') {
            endpoint = `posTerminalId=${encodeURIComponent(query)}`
          }

          if (endpoint) {
            const response: any = await posService.searchPosAccount(endpoint, pageNumber, 'DESC')
            if (cancelled) return
            setAccounts(response?.data?.data?.dataset ?? [])
            onTotalChange(response?.data?.data?.total ?? 0, response?.data?.data?.pageSize)
            return
          }
        }

        const response: any = await posService.getAccountsWithPosTerminal(
          pageNumber,
          'DESC',
          undefined,
          posFrom,
          posTo,
          posTerminalId,
          posProvider,
        )
        if (cancelled) return
        setAccounts(response?.data?.data?.dataset ?? [])
        onTotalChange(response?.data?.data?.total ?? 0, response?.data?.data?.pageSize)
      } catch {
        if (!cancelled) {
          setAccounts([])
          onTotalChange(0)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
          onLoadingChange?.(false)
        }
      }
    }
    fetchPosAccounts()
    return () => {
      cancelled = true
    }
  }, [
    pageNumber,
    searchTerm,
    posFrom,
    posTo,
    posTerminalId,
    posProvider,
    onTotalChange,
    onLoadingChange,
  ])

  return (
    <div className='bx-table-wrapper'>
      <table className='bx-table'>
        <thead>
          <tr>
            <th className='bx-finance__col-index'>#</th>
            <th className='bx-finance__col-user'>POS Terminal ID</th>
            <th className='bx-finance__col-reference'>Account ID</th>
            <th className='bx-finance__col-amount'>Auth Code</th>
            <th className='bx-finance__col-status'>Status</th>
            <th className='bx-finance__col-type'>Provider</th>
            <th className='bx-finance__col-date'>Created At</th>
            <th className='bx-finance__col-action'>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={8} className='bx-finance__empty'>
                Loading...
              </td>
            </tr>
          ) : accounts.length ? (
            accounts.map((account, index) => {
              const offset = (pageNumber - 1) * pageSize
              return (
                <tr key={account.id}>
                  <td className='bx-finance__col-index'>{offset + index + 1}</td>
                  <td className='bx-finance__col-user'>{account.posTerminalId}</td>
                  <td className='bx-finance__col-reference'>{account.accountId}</td>
                  <td className='bx-finance__col-amount'>{account.authorizationCode ?? '-'}</td>
                  <td className='bx-finance__col-status'>
                    <span
                      className={`bx-status-pill bx-status-pill--${account.isActive ? 'active' : 'inactive'}`}
                    >
                      {account.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className='bx-finance__col-type'>{account.provider}</td>
                  <td className='bx-finance__col-date'>
                    {account.createdAt ? new Date(account.createdAt).toLocaleString() : '-'}
                  </td>
                  <td className='bx-finance__col-action'>
                    <button
                      className='bx-view-link btn btn-sm btn-default'
                      onClick={() =>
                        navigate(
                          `/boostxpress/bx-pos-terminal-transactions/${String(account.posTerminalId ?? '').toUpperCase()}`,
                        )
                      }
                    >
                      View
                    </button>
                  </td>
                </tr>
              )
            })
          ) : (
            <tr>
              <td colSpan={8} className='bx-finance__empty'>
                No POS terminals found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default PosTerminalsTable
